import React from 'react';
import { EAdCreativeMediaType } from 'types/ad-campaign';

import { ButtonBase, Dialog, useTheme } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import BrokenImageOutlinedIcon from '@material-ui/icons/BrokenImageOutlined';
import CloseIcon from '@material-ui/icons/Close';
import PlayCircleOutlineIcon from '@material-ui/icons/PlayCircleOutline';
import { Skeleton } from '@material-ui/lab';

import { useAuthDataContext } from 'components/AuthDataProvider/AuthDataProvider';
import { useUserInfoContext } from 'components/UserInfoProvider/UserInfoProvider';

import { isMarketingProfileCompleted } from 'utils/lib';

const useStyles = makeStyles((theme) => ({
  image: {
    display: 'block',
    width: '100%',
    maxWidth: '350px',
    maxHeight: '350px',
    objectFit: 'contain',
    borderRadius: '8px',
  },
  video: {
    display: 'block',
    width: '100%',
    maxWidth: '350px',
    maxHeight: '350px',
    backgroundColor: theme.palette.black.main,
    borderRadius: '8px',
    pointerEvents: 'none',
  },
  videoPreviewButton: {
    position: 'relative',
    display: 'block',
    width: '100%',
    maxWidth: '350px',
    maxHeight: '350px',
    overflow: 'hidden',
    borderRadius: '8px',
    '&:hover $videoPlayIndicator': {
      backgroundColor: 'rgba(0, 0, 0, 0.28)',
    },
    '&:focus-visible': {
      outline: `3px solid ${theme.palette.primary.main}`,
      outlineOffset: '3px',
    },
  },
  hiddenMedia: {
    display: 'none',
  },
  videoPlayIndicator: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.white.main,
    backgroundColor: 'rgba(0, 0, 0, 0.16)',
    pointerEvents: 'none',
    transition: 'background-color 180ms ease',
    '& svg': {
      width: '60px',
      height: '60px',
      filter: 'drop-shadow(0 3px 10px rgba(0, 0, 0, 0.5))',
    },
  },
  skeleton: {
    width: '100%',
    height: '350px',
    borderRadius: '8px',
    [theme.breakpoints.down('xs')]: {
      height: '260px',
    },
  },
  unavailableState: {
    boxSizing: 'border-box',
    width: '100%',
    minHeight: '220px',
    padding: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    color: theme.palette.lightGray.main,
    backgroundColor: theme.palette.grayScale7.main,
    border: `1px dashed ${theme.palette.grayScale5.main}`,
    borderRadius: '8px',
    '& svg': {
      width: '42px',
      height: '42px',
      marginBottom: theme.spacing(1),
      color: theme.palette.grayScale4.main,
    },
  },
  unavailableTitle: {
    color: theme.palette.darkGray.main,
    fontSize: '15px',
    lineHeight: '21px',
    fontWeight: 600,
  },
  unavailableText: {
    marginTop: '4px',
    fontSize: '13px',
    lineHeight: '19px',
  },
  popupDialog: {
    '& .MuiDialog-container': {
      alignItems: 'center',
      justifyContent: 'center',
    },
  },
  popupPaper: {
    margin: theme.spacing(2),
    width: 'fit-content',
    height: 'fit-content',
    maxWidth: 'min(800px, calc(100vw - 136px))',
    maxHeight: `min(800px, calc(100vh - ${theme.spacing(4)}px))`,
    backgroundColor: 'transparent',
    boxShadow: 'none',
    overflow: 'visible',
  },
  popupContent: {
    position: 'relative',
    display: 'inline-flex',
    maxWidth: '100%',
    maxHeight: '100%',
  },
  popupMedia: {
    display: 'block',
    width: '100%',
    height: '100%',
    maxWidth: 'min(800px, calc(100vw - 136px))',
    maxHeight: `min(800px, calc(100vh - ${theme.spacing(4)}px))`,
    objectFit: 'contain',
    borderRadius: '10px',
    boxShadow: '0 18px 60px rgba(0, 0, 0, 0.38)',
  },
  popupCloseButton: {
    position: 'absolute',
    zIndex: 1,
    top: 0,
    right: '-30px',
    width: '44px',
    height: '44px',
    borderRadius: '50%',
    color: theme.palette.darkGray.main,
    backgroundColor: 'rgba(255, 255, 255, 0.94)',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.24)',
    transition: 'background-color 180ms ease, box-shadow 180ms ease',
    '&:hover': {
      backgroundColor: theme.palette.white.main,
      boxShadow: '0 6px 20px rgba(0, 0, 0, 0.3)',
    },
    '&:focus-visible': {
      outline: `3px solid ${theme.palette.primary.main}`,
      outlineOffset: '2px',
    },
  },
  popupBackdrop: {
    backgroundColor: 'rgba(10, 12, 14, 0.8)',
    backdropFilter: 'blur(4px)',
    WebkitBackdropFilter: 'blur(4px)',
  },
}));

interface Props {
  imageData?: string;
  mediaUrl?: string;
  mediaType?: EAdCreativeMediaType;
  videoThumbnailUrl?: string;
  alt?: string;
  isLoading?: boolean;
  showMarketingProfileNotice?: boolean;
}

type MediaStatus = 'loading' | 'ready' | 'error';

export default ({
  imageData,
  mediaUrl,
  mediaType = EAdCreativeMediaType.Image,
  videoThumbnailUrl,
  alt = 'Ad preview',
  isLoading = false,
  showMarketingProfileNotice = true,
}: Props) => {
  const theme = useTheme();
  const classes = useStyles();

  const { userInfo } = useUserInfoContext() as any;
  const { isAnyAgent } = useAuthDataContext() as any;

  const resolvedMediaUrl = mediaUrl ?? imageData;
  const isVideo = mediaType === EAdCreativeMediaType.Video;
  const [popupOpen, setPopupOpen] = React.useState(false);
  const [mediaStatus, setMediaStatus] = React.useState<MediaStatus>(
    resolvedMediaUrl ? 'loading' : 'ready',
  );

  React.useEffect(() => {
    setPopupOpen(false);
    setMediaStatus(resolvedMediaUrl ? 'loading' : 'ready');
  }, [mediaType, resolvedMediaUrl]);

  const onMediaReady = () => setMediaStatus('ready');
  const onMediaError = () => {
    setPopupOpen(false);
    setMediaStatus('error');
  };

  const showSkeleton = isLoading || (!!resolvedMediaUrl && mediaStatus === 'loading');
  const showUnavailable = !isLoading && (!resolvedMediaUrl || mediaStatus === 'error');
  const showMedia = !!resolvedMediaUrl && mediaStatus === 'ready';

  return (
    <>
      {popupOpen && resolvedMediaUrl && (
        <Dialog
          id="preview-modal"
          open
          keepMounted
          maxWidth={false}
          onClose={() => setPopupOpen(false)}
          aria-label="Ad preview"
          className={classes.popupDialog}
          PaperProps={{ className: classes.popupPaper }}
          BackdropProps={{ className: classes.popupBackdrop }}
        >
          <ButtonBase
            className={classes.popupCloseButton}
            onClick={() => setPopupOpen(false)}
            aria-label="Close ad preview"
          >
            <CloseIcon />
          </ButtonBase>
          <div className={classes.popupContent}>
            {isVideo ? (
              <video
                className={classes.popupMedia}
                src={resolvedMediaUrl}
                poster={videoThumbnailUrl}
                controls
                autoPlay
                playsInline
                onError={onMediaError}
                aria-label={alt}
              />
            ) : (
              <img
                className={classes.popupMedia}
                src={resolvedMediaUrl}
                onError={onMediaError}
                alt={alt}
              />
            )}
          </div>
        </Dialog>
      )}

      {showMarketingProfileNotice && isAnyAgent && !isMarketingProfileCompleted(userInfo?.agentInfo) && (
        <div style={{
          marginTop: '6px',
          fontSize: '16px',
          lineHeight: '20px',
          fontWeight: 400,
          color: theme.palette.lightGray.main,
          marginBottom: '10px',
        }}
        >
          Your information will show in the preview after you complete your marketing profile.
        </div>
      )}

      <div aria-live="polite">
        {showSkeleton && <Skeleton variant="rect" className={classes.skeleton} />}

        {showUnavailable && (
          <div className={classes.unavailableState} role={mediaStatus === 'error' ? 'alert' : undefined}>
            <BrokenImageOutlinedIcon aria-hidden />
            <div className={classes.unavailableTitle}>
              {mediaStatus === 'error' ? 'Preview could not be loaded' : 'Preview not available yet'}
            </div>
            <div className={classes.unavailableText}>
              {mediaStatus === 'error'
                ? 'The creative may still be processing. Try refreshing the page in a moment.'
                : 'The creative will appear here when it is ready.'}
            </div>
          </div>
        )}

        {resolvedMediaUrl && (
          <ButtonBase
            className={isVideo
              ? `${classes.videoPreviewButton} ${showMedia ? '' : classes.hiddenMedia}`
              : (showMedia ? undefined : classes.hiddenMedia)}
            onClick={() => setPopupOpen(true)}
            aria-label={isVideo ? 'Open a larger video preview' : 'Open a larger ad preview'}
          >
            {isVideo ? (
              <>
                <video
                  className={classes.video}
                  src={resolvedMediaUrl}
                  poster={videoThumbnailUrl}
                  preload="metadata"
                  muted
                  playsInline
                  onLoadedMetadata={onMediaReady}
                  onError={onMediaError}
                  aria-hidden
                />
                <span className={classes.videoPlayIndicator} aria-hidden>
                  <PlayCircleOutlineIcon />
                </span>
              </>
            ) : (
              <img
                className={classes.image}
                src={resolvedMediaUrl}
                onLoad={onMediaReady}
                onError={onMediaError}
                alt={alt}
              />
            )}
          </ButtonBase>
        )}
      </div>
    </>
  );
};
